import { Box, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { StarOutlineIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

import { RPL_BODY, RPL_EYEBROW, RPL_HEADLINE, RPL_LP_QUOTES, RPL_LP_SECTION_ID } from '../constants';

import { RplCalculateButton } from './rpl-calculate-button';
import { RplQuoteCard } from './rpl-quote-card';

const TILE_GAP = { base: '8px', md: '16px' };

export function AccreditationRplSection({
  formalDegreeKey,
  isForcedHidden = false,
}: {
  formalDegreeKey: FormalDegreeKey;
  isForcedHidden?: boolean;
}) {
  if (isForcedHidden) {
    return null;
  }

  const isMba = formalDegreeKey === 'mba_ai';
  const quotes = isMba ? [] : RPL_LP_QUOTES;
  const headline = isMba ? RPL_HEADLINE.mba : RPL_HEADLINE.masters;

  return (
    <ContentContainer
      aria-label='Recognition of prior learning'
      as='section'
      contentMaxWidth='content-max-width-md'
      id={RPL_LP_SECTION_ID}
      mt={TILE_GAP}
      scrollMarginTop={{ base: '72px', md: '96px' }}
    >
      <Flex
        bg='grey.50'
        border='1px solid'
        borderColor='grey.100'
        borderRadius='4px'
        direction='column'
        gap={{ base: '20px', md: '24px' }}
        p={{ base: '14px 16px', md: '24px' }}
      >
        <Flex
          alignItems={{ md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: '16px', md: '32px' }}
        >
          <Flex flex={1} gap='24px' minW={0}>
            <StarOutlineIcon
              color='gold.500'
              flexShrink={0}
              h={{ base: '18px', md: '24px' }}
              w={{ base: '18px', md: '24px' }}
            />
            <Box alignSelf='stretch' bgGradient='linear(to-b, #EDA93B 0%, transparent 100%)' flexShrink={0} w='2px' />
            <Flex direction='column' gap='6px' minW={0}>
              <Heading
                as='p'
                color='gold.500'
                fontFamily='condensed'
                fontSize='0.9375rem'
                fontWeight='500'
                letterSpacing='0.8px'
                lineHeight='1'
                textTransform='uppercase'
              >
                {RPL_EYEBROW}
              </Heading>
              <Heading as='h2' fontWeight='600' size={{ base: 'heading-9', md: 'heading-8' }}>
                {headline}
              </Heading>
              <Text color='grey.900' fontSize='sm'>
                {RPL_BODY}
              </Text>
            </Flex>
          </Flex>
          <Flex flexShrink={0} w={{ base: '100%', md: 'auto' }}>
            <RplCalculateButton
              eventName='Masters Page CTA Clicked'
              formalDegreeKey={formalDegreeKey}
              width={{ base: '100%', md: 'fit-content' }}
            />
          </Flex>
        </Flex>
        {quotes.length > 0 ? (
          <>
            <Box bg='grey.100' h='1px' />
            <SimpleGrid columns={{ base: 1, md: 2 }} gap='24px'>
              {quotes.map((quote) => (
                <RplQuoteCard key={quote.name} quote={quote} />
              ))}
            </SimpleGrid>
          </>
        ) : null}
      </Flex>
    </ContentContainer>
  );
}
